import { useMutation } from '@tanstack/react-query'
import { baseUrl } from 'api/config'
import axios from 'axios'

/**
 * React-query mutation for the bulk-patch endpoint.
 *
 * One hook covers both dry-run (preview) and apply paths — the caller
 * sets `dryRun: false` to actually write. The page keeps preview +
 * apply results in separate `useState` slots so the user can see both.
 *
 * Server contract: `POST /aws/deals/bulk-patch`
 *   Body: { rows, dryRun?, currentUser? }
 *   Response: BulkPatchResult (counts + per-row results)
 */

export type BlockReason =
  | 'org_not_found'
  | 'org_has_no_active_deal'
  | 'deal_not_found_at_uuid'
  | 'deal_terminal'
  | 'product_not_found_on_deal'
  | 'no_changes_specified'

/**
 * How the patch lands at apply time. Mirrors the BE.
 *   - change_order: in-place semver bump on the same deal record.
 *   - new_contract: successor deal record with predecessorId; old deal
 *     flips to Replaced; new no-signature contract auto-created.
 */
export type PatchClassification = 'change_order' | 'new_contract'

export interface BulkPatchInputRow {
  row_index?: number
  grobotic_org_pk: string
  deal_uuid?: string
  product_pk: string
  product_name?: string
  new_price?: number | null
  new_margin?: number | null
  new_start_date?: string | null
  new_end_date?: string | null
}

export interface ProductDiffEntry {
  product_pk: string
  product_name: string
  field: 'price' | 'margin' | 'startDate' | 'endDate'
  old_value: number | string | null
  new_value: number | string | null
}

export interface BulkPatchRowResult {
  row_index?: number
  grobotic_org_pk: string
  org_name?: string
  resolved_deal_sk?: string
  resolved_deal_uuid?: string
  resolved_deal_name?: string
  status: 'applied' | 'would_apply' | 'blocked' | 'error'
  reason?: BlockReason
  message?: string
  diff: ProductDiffEntry[]
  new_semantic_version?: string
  /** Per-deal classification — same value across every row in the same deal group. */
  classification?: PatchClassification
  heartbeat_push?: {
    success: boolean
    flow?: string
    skip_reason?: string
    emitted_submissions?: number
  }
}

export interface BulkPatchResult {
  success: boolean
  dry_run: boolean
  total_rows: number
  applied: number
  would_apply: number
  blocked: number
  errored: number
  results: BulkPatchRowResult[]
}

interface BulkPatchPayload {
  rows: BulkPatchInputRow[]
  dryRun: boolean
  currentUser?: {
    user_uuid?: string
    id?: string
    email?: string
    name?: string
  }
}

export const useBulkPatch = (): ReturnType<
  typeof useMutation<BulkPatchResult, Error, BulkPatchPayload>
> => {
  return useMutation<BulkPatchResult, Error, BulkPatchPayload>({
    mutationFn: async (payload): Promise<BulkPatchResult> => {
      const response = await axios.post<BulkPatchResult>(
        `${baseUrl}/aws/deals/bulk-patch`,
        payload,
      )
      return response.data
    },
  })
}
